import React, { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { stopProductEvents } from '../events/music24Events';
import { TypedMemo } from '../../common/types';
import { websitePageUrl } from '../../app/locale/websitePath';
import { useIsNativeEmbed } from '../../music24/navigation/hooks/useIsNativeEmbed';
import { isCoverMakerEmbedLocation } from '../../coverEditor/publicMode/publicCoverPath';
import { isMusicVideoDemoEmbedLocation } from '../../musicVideoEditor/showcase/musicVideoDemoPath';
import {
  clearTrackerCookies,
  OPEN_TRACKING_CONSENT_EVENT,
  setTrackingConsent,
  TrackingConsent,
} from './trackingConsent';
import { useTrackingConsent } from './useTrackingConsent';
import { useConsentBarSpace } from './useConsentBarSpace';
import { SMLGenericAnalytics } from '../sml/genericAnalytics';
import styles from './TrackingConsentBar.module.scss';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';

const messages = defineMessages({
  title: { id: 'app.sml.consent.title', defaultMessage: 'Cookies on MUSIC24' },
  body: {
    id: 'app.sml.consent.body',
    defaultMessage:
      'We use cookies to understand how people use MUSIC24 and to make it better. They load only when you accept.',
  },
  privacy: { id: 'app.sml.consent.privacy', defaultMessage: 'Privacy policy' },
  reject: { id: 'app.sml.consent.reject', defaultMessage: 'Reject' },
  accept: { id: 'app.sml.consent.accept', defaultMessage: 'Accept' },
  label: { id: 'app.sml.consent.label', defaultMessage: 'Cookie choice' },
});

/**
 * The consent bar of the web app (design: docs/superpowers/design-reference/music24-consent, a3 + a4).
 * It reads the same stored choice as music24.com, so a choice made on the website applies here.
 * It shows while there is no choice, and again after Settings → Legal → Cookie settings.
 * While it shows, the page gets bottom padding as tall as the bar (`useConsentBarSpace`), so no
 * content stays under it; on a phone it sits above the bottom tab bar.
 * It never shows inside a native app's web view: there nothing non-essential loads. It never shows
 * in the cover maker or the music video demo inside the frame of music24.com either
 * (`/app/cover-maker?embed=1`, `/app/music-video-demo?embed=1`): the page around the frame owns the
 * choice, and the stored choice applies here (same origin and key).
 */
const TrackingConsentBarComponent: React.FC = () => {
  const intl = useIntl();
  const isNativeEmbed = useIsNativeEmbed();
  const consent = useTrackingConsent();
  const [reopened, setReopened] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOpen = (): void => setReopened(true);
    window.addEventListener(OPEN_TRACKING_CONSENT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_TRACKING_CONSENT_EVENT, onOpen);
  }, []);

  // A choice made in another tab (or on the website) applies here too.
  useEffect(() => {
    if (consent === 'granted') {
      SMLGenericAnalytics.enableTracking();
      return;
    }
    if (consent !== 'denied') {
      return;
    }
    // Cookies left from an earlier Accept go with the Reject. So do the stored product events
    // queue and install id, also when no tracker runs on this page view (a Reject on music24.com).
    clearTrackerCookies();
    stopProductEvents();
    if (SMLGenericAnalytics.isTrackingOn()) {
      SMLGenericAnalytics.disableTracking();
      window.location.reload();
    }
  }, [consent]);

  const choose = useCallback((choice: TrackingConsent) => {
    setReopened(false);
    setTrackingConsent(choice);
  }, []);
  const onAccept = useCallback(() => {
    choose('granted');
    // The effect above starts the trackers first (same render pass), so this is the first event.
    window.setTimeout(() => {
      SMLGenericAnalytics.track('analytics_consent_given', {}).catch(console.error);
    }, 0);
  }, [choose]);
  const onReject = useCallback(() => choose('denied'), [choose]);

  const isFramedTool =
    typeof window !== 'undefined' &&
    (isCoverMakerEmbedLocation(window.location) || isMusicVideoDemoEmbedLocation(window.location));
  const isHidden = isNativeEmbed || isFramedTool || (consent !== null && !reopened);
  // The bar never covers the page: while it shows, the page gets room at its foot.
  useConsentBarSpace(barRef, !isHidden);
  if (isHidden) {
    return null;
  }
  return (
    <div ref={barRef} className={styles.bar} role="dialog" aria-label={intl.formatMessage(messages.label)}>
      <div className={styles.text}>
        <b>{withBrandWord(intl.formatMessage(messages.title))}</b>
        <p>
          {withBrandWord(intl.formatMessage(messages.body))}{' '}
          {/* The policy in the language of the app once music24.com has it (QA lit-20). */}
          <a href={websitePageUrl('privacy-policy', intl.locale)} target="_blank" rel="noopener noreferrer">
            {intl.formatMessage(messages.privacy)}
          </a>
        </p>
      </div>
      <div className={styles.actions}>
        <button type="button" className={`${styles.button} ${styles.reject}`} onClick={onReject}>
          {intl.formatMessage(messages.reject)}
        </button>
        <button type="button" className={`${styles.button} ${styles.accept}`} onClick={onAccept}>
          {intl.formatMessage(messages.accept)}
        </button>
      </div>
    </div>
  );
};

export const TrackingConsentBar = TypedMemo(TrackingConsentBarComponent);
